// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import fieldDefinitions from "@bg/App/Widgets/SearchEngineOptimization/Fields";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import * as React from "react";
import { connect } from "react-redux";

// Extentions
import Brand from "@bg/App/Widgets/SearchEngineOptimization/SchemaBrand";
import BreadcrumbList from "@bg/App/Widgets/SearchEngineOptimization/SchemaBreadcrumbList";
import CollectionPage from "@bg/App/Widgets/SearchEngineOptimization/SchemaCollectionPage";
import ContactPage from "@bg/App/Widgets/SearchEngineOptimization/SchemaContactPage";
import LocalBusiness from "@bg/App/Widgets/SearchEngineOptimization/SchemaLocalBusiness";
import Organization from "@bg/App/Widgets/SearchEngineOptimization/SchemaOrganization";
import Product from "@bg/App/Widgets/SearchEngineOptimization/SchemaProduct";
import SiteNavigationElement from "@bg/App/Widgets/SearchEngineOptimization/SchemaSiteNavigationElement";
import WebPage from "@bg/App/Widgets/SearchEngineOptimization/SchemaWebPage";
import Website from "@bg/App/Widgets/SearchEngineOptimization/SchemaWebSite";

export interface Jsonld {
    [key: string]: string | number | Jsonld | Jsonld[] | Jsonld[][] | undefined;
}
export type ActiveState = string | number | boolean | undefined;
export interface SeoProps {
    state: ApplicationState;
    fields: any;
    currentPage: string;
    url: string;
    payload: { [key: string]: any };
}

interface SeoExtention {
    activate: (props: SeoProps) => ActiveState;
    json: (props: SeoProps) => Jsonld;
}

const mapStateToProps = (state: ApplicationState) => ({
    state,
    currentPage: getCurrentPage(state)?.name,
    url: `https://${state?.context?.website?.uri?.split("/")[2].split(":")[0]}`,
    payload: {},
});

const SeoGenerator = (props: SeoProps & HasShellContext) => {
    if (props.shellContext.isInShell) {
        // to prevent white screen when authoring the widget
        return <></>;
    }
    const use = (extention: SeoExtention) => !!extention.activate(props) && <Jsonld json={extention.json(props)} />;

    return (
        <>
            {use(Organization)}
            {use(Website)}
            {use(WebPage)}
            {use(ContactPage)}
            {use(LocalBusiness)}
            {use(BreadcrumbList)}
            {use(SiteNavigationElement)}
            {use(CollectionPage)}
            {use(Product)}
            {use(Brand)}
        </>
    );
};

const Jsonld = ({ json }: { json: Jsonld }) => {
    // eslint-disable-next-line react/no-danger
    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
};

const definition: WidgetDefinition = {
    group: "Header",
    displayName: "Search Engine Optimization",
    allowedContexts: ["Header"],
    fieldDefinitions,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(SeoGenerator)),
    definition,
};

export default widgetModule;
